<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon> </v-btn
          >Edit Reward
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="4">
              <v-autocomplete
                v-model="form.week_id"
                :items="weekItems"
                readonly
                outlined
                label="Week"
                prepend-inner-icon="mdi-calendar-week"
                clearable
              ></v-autocomplete>
            </v-col>
            <v-col sm="4">
              <v-switch
                class="mt-0 ml-5"
                v-model="form.is_active"
                inset
                :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
              ></v-switch>
            </v-col>
          </v-row>
          <v-divider class="my-5"> </v-divider>

          <section v-if="form.week_id">
            <!-- Days Section -->
            <div v-for="(day, dayIndex) in form.days" :key="`day${dayIndex}`">
              <v-card class="mb-4" outlined>
                <v-card-title class="py-2">
                  <v-row align="center">
                    <v-col sm="4">
                      <v-autocomplete
                        v-model="day.day_number_id"
                        :items="availableDayItems(dayIndex)"
                        outlined
                        readonly
                        dense
                        label="Day Number"
                        prepend-inner-icon="mdi-calendar-today"
                        clearable
                      ></v-autocomplete>
                    </v-col>
                    <v-col sm="2">
                      <v-dialog
                        v-model="isDeleteDayDialogs[dayIndex]"
                        max-width="600px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            small
                            dark
                            color="red"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small> mdi-delete </v-icon>
                            Remove Day
                          </v-btn>
                        </template>
                        <v-card elevation="10">
                          <v-card-title>
                            <v-icon large color="primary">
                              mdi-information-outline
                            </v-icon>
                            &nbsp; Do you want to remove this day?
                          </v-card-title>
                          <v-card-text>
                            <v-btn
                              color="primary"
                              @click.stop="deleteDay(dayIndex)"
                            >
                              Yes
                            </v-btn>
                            &nbsp;
                            <v-btn
                              color="red"
                              dark
                              @click.stop="isDeleteDayDialogs = []"
                            >
                              No
                            </v-btn>
                          </v-card-text>
                        </v-card>
                      </v-dialog>
                    </v-col>
                  </v-row>
                </v-card-title>

                <v-card-text v-if="day.day_number_id">
                  <h4 class="mb-3">
                    Rewards for Day {{ getDayText(day.day_number_id) }}
                  </h4>

                  <!-- Rewards for this day -->
                  <v-row
                    v-for="(reward, rewardIndex) in day.rewards"
                    :key="`reward${dayIndex}_${rewardIndex}`"
                  >
                    <v-col sm="3">
                      <v-autocomplete
                        v-model="reward.reward_type_id"
                        :items="rewardTypesItems"
                        outlined
                        dense
                        label="Reward Type"
                        prepend-inner-icon="mdi-gift"
                        clearable
                      ></v-autocomplete>
                    </v-col>
                    <v-col sm="3" v-if="reward.reward_type_id == 25">
                      <v-text-field
                        v-model="reward.reward_value"
                        outlined
                        dense
                        label="Reward Value"
                        type="number"
                        prepend-inner-icon="mdi-currency-usd"
                      ></v-text-field>
                    </v-col>
                    <v-col sm="2" v-if="reward.reward_type_id == 24">
                      <v-autocomplete
                        v-model="reward.store_item_id"
                        :items="storeItems"
                        item-text="name"
                        item-value="id"
                        outlined
                        dense
                        label="Store Item"
                        prepend-inner-icon="mdi-package-variant"
                        clearable
                      />
                    </v-col>
                    <v-col sm="2" v-if="reward.reward_type_id == 24">
                      <v-text-field
                        v-model="reward.expiry_date"
                        outlined
                        dense
                        label="Expiry Date"
                        type="date"
                        prepend-inner-icon="mdi-calendar-clock"
                        :min="minDate"
                        required
                      />
                    </v-col>
                    <v-col sm="1">
                      <v-dialog
                        v-model="
                          isDeleteRewardDialogs[`${dayIndex}_${rewardIndex}`]
                        "
                        max-width="600px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            fab
                            small
                            dark
                            color="red"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon dark> mdi-close-thick </v-icon>
                          </v-btn>
                        </template>
                        <v-card elevation="10">
                          <v-card-title>
                            <v-icon large color="primary">
                              mdi-information-outline
                            </v-icon>
                            &nbsp; Do you want to remove this reward?
                          </v-card-title>
                          <v-card-text>
                            <v-btn
                              color="primary"
                              @click.stop="deleteReward(dayIndex, rewardIndex)"
                            >
                              Yes
                            </v-btn>
                            &nbsp;
                            <v-btn
                              color="red"
                              dark
                              @click.stop="isDeleteRewardDialogs = {}"
                            >
                              No
                            </v-btn>
                          </v-card-text>
                        </v-card>
                      </v-dialog>
                    </v-col>
                  </v-row>

                  <!-- Add Reward Button for this day -->
                  <v-row>
                    <v-col sm="12" class="mt-2">
                      <v-btn
                        x-small
                        rounded
                        color="secondary"
                        @click="addRewardToDay(dayIndex)"
                      >
                        Add Reward
                        <v-icon small dark> mdi-plus </v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </div>

            <!-- Add Day Button -->
            <v-row>
              <v-col sm="12" class="mb-5">
                <v-btn small rounded color="primary" @click="addNewDay">
                  Add Day
                  <v-icon dark> mdi-plus </v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-divider class="mt-5 mb-5"> </v-divider>
          </section>
          <v-btn
            rounded
            color="primary"
            :loading="isLoading"
            :disabled="isLoading"
            @click="save"
          >
            Update Rewards
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- Success Dialog -->
      <v-dialog v-model="successDialog" width="500">
        <v-card rounded="xxl">
          <v-card-title class="headline text-center">
            <v-icon large color="success" class="mr-2">mdi-check-circle</v-icon>
            Success!
          </v-card-title>
          <v-card-text class="text-center">
            <h3>Rewards updated successfully!</h3>
          </v-card-text>
          <v-card-actions class="justify-center">
            <v-btn color="success" @click="handleSuccessClose"> OK </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>
<script>
import axios from "axios";

export default {
  name: "RewardIdPage",
  data() {
    return {
      filter: {
        search_keyword: "",
        is_active: "",
      },
      form: {
        days: [],
        week_id: "",
        is_active: true,
      },
      isLoading: false,
      dayItems: [],
      rewardTypesItems: [],
      weekItems: [],
      isDeleteRewardDialogs: {},
      isDeleteDayDialogs: [],
      old_json: {},
      successDialog: false,
      filesToUpload: {}, // Track files to upload for each reward
      storeItems: [],
      minDate: new Date().toISOString().split("T")[0],
    };
  },
  mounted() {
    this.getMasters();
    this.getData();
  },
  methods: {
    async getMasters() {
      await axios
        .get(`masters?context=rewards`)
        .then((masters) => {
          masters = masters.data;

          // Days
          masters.days.forEach((day) => {
            this.dayItems.push({
              id: day.id,
              text: day.description,
              value: day.id,
            });
          });

          // Weeks
          masters.weeks.forEach((week) => {
            this.weekItems.push({
              id: week.id,
              text: week.description,
              value: week.id,
            });
          });

          // Reward Types
          masters.reward_types.forEach((reward_type) => {
            this.rewardTypesItems.push({
              id: reward_type.id,
              text: reward_type.description,
              value: reward_type.id,
            });
          });
          console.log("storeItem", masters.storeItem);

          // Store Items
          masters.storeItem.forEach((item) => {
            this.storeItems.push({
              id: item.id,
              name: item.name,
              value: item.id,
            });
          });
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    async getData() {
      this.isLoading = true;
      this.filter.search_keyword = this.filter.search_keyword
        ? this.filter.search_keyword
        : "";
      console.log("route", this.$route);

      if (this.$route.params.weekId) {
        this.filter.week_id = this.decryptIt(this.$route.params.weekId);
      } else {
        this.filter.week_id = "";
      }
      let query =
        "week_id=" +
        this.filter.week_id +
        "&search_keyword=" +
        this.filter.search_keyword;

      if (this.filter.is_active !== null) {
        query += "&is_active=" + this.filter.is_active;
      }
      if (this.filter.company_id !== null) {
        query += "&company_id=" + this.filter.company_id;
      }

      await axios
        .get(`daily_rewards?${query}`)
        .then((response) => {
          const rewards = response.data.data;

          // Set week_id from the first reward or from filter
          if (rewards.length > 0 && rewards[0].week_id) {
            this.form.week_id = rewards[0].week_id;
            this.form.is_active = rewards[0].is_active;
          } else if (this.filter.week_id) {
            this.form.week_id = parseInt(this.filter.week_id);
          }

          // Transform flat rewards to hierarchical structure (days with rewards)
          const daysMap = {};
          rewards.forEach((reward) => {
            if (!daysMap[reward.day_number_id]) {
              daysMap[reward.day_number_id] = {
                day_number_id: reward.day_number_id,
                rewards: [],
              };
            }
            daysMap[reward.day_number_id].rewards.push({
              id: reward.id,
              reward_type_id: reward.reward_type_id,
              reward_value: reward.reward_value,
              store_item_id: reward.store_item_id,
              expiry_date: reward.expiry_date,
            });
          });

          this.form.days = Object.values(daysMap);
          this.old_json = JSON.parse(JSON.stringify(this.form));

          console.log("rewards", this.form);

          this.count = response.data.count;
          this.serialNoStarting = (this.page - 1) * this.rowsPerPage;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
        })
        .catch(function (error) {
          console.log(error);
        });
      this.isLoading = false;
    },

    async save() {
      try {
        this.isLoading = true;

        // Flatten the days structure to rewards array for API
        const rewards = [];
        this.form.days.forEach((day) => {
          day.rewards.forEach((reward) => {
            rewards.push({
              id: reward.id, // Include ID for updates
              day_number_id: day.day_number_id,
              reward_type_id: reward.reward_type_id,
              reward_value: reward.reward_value,
              store_item_id: reward.store_item_id,
              expiry_date: reward.expiry_date,
            });
          });
        });

        const payload = {
          week_id: this.form.week_id,
          is_active: this.form.is_active,
          rewards: rewards,
        };

        console.log("Payload being sent:", payload);
        console.log("Number of rewards:", rewards.length);

        // Update the main reward data
        const response = await axios.patch(
          `/daily_rewards/${this.$route.params.rewardId}`,
          payload
        );

        // Handle file uploads for each reward if needed
        for (let index = 0; index < rewards.length; index++) {
          const reward = rewards[index];
          if (this.filesToUpload[index]) {
            await this.handleFileUpload(
              reward.id || response.data.data.id,
              this.filesToUpload[index]
            );
          }
        }

        await this.saveUserTimestamp(this.old_json, this.form);
        this.successDialog = true;
        this.isLoading = false;
      } catch (e) {
        console.log(e);
        this.isLoading = false;
      }
    },

    async handleFileUpload(rewardId, file) {
      let formData = new FormData();
      formData.append("id", rewardId);
      formData.append("reward_image", file);

      await axios
        .post("upload_rewards_image", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .catch(function (error) {
          console.log(error);
          console.log("FAILURE!!");
        });
    },

    onFileChange(index) {
      const fileInput = this.$refs[`file_${index}`][0];
      const file = fileInput?.files[0];

      if (file) {
        // Store the file to upload later
        this.filesToUpload[index] = file;

        let reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (evt) => {
          let img = new Image();
          img.onload = () => {
            this.form.rewards[index].image_path = URL.createObjectURL(file);
            this.form.rewards[index].reward_image = null; // Clear old image reference
          };
          img.src = evt.target.result;
        };
        reader.onerror = (evt) => {
          console.error(evt);
        };
      }
    },

    removeImage(index) {
      if (this.$refs[`file_${index}`]) {
        this.$refs[`file_${index}`][0].value = null;
      }
      delete this.filesToUpload[index];
    },

    addNewDay() {
      let day = {
        day_number_id: "",
        rewards: [],
      };
      this.form.days.push(day);
    },

    addRewardToDay(dayIndex) {
      let reward = {
        reward_type_id: "",
        reward_value: "",
        store_item_id: "",
        expiry_date: "",
      };
      this.form.days[dayIndex].rewards.push(reward);
    },

    deleteDay(dayIndex) {
      this.form.days.splice(dayIndex, 1);
      this.isDeleteDayDialogs = [];
    },

    deleteReward(dayIndex, rewardIndex) {
      this.form.days[dayIndex].rewards.splice(rewardIndex, 1);
      this.isDeleteRewardDialogs = {};
    },

    availableDayItems(currentDayIndex) {
      const selectedDays = this.form.days
        .map((day, index) =>
          index !== currentDayIndex ? day.day_number_id : null
        )
        .filter((id) => id !== null);

      return this.dayItems.filter((item) => !selectedDays.includes(item.value));
    },

    getDayText(dayId) {
      const day = this.dayItems.find((item) => item.value === dayId);
      return day ? day.text : "";
    },

    async saveUserTimestamp(oldData, newData) {
      try {
        let form = {
          from_path: `/daily_rewards/${this.$route.params.rewardId}`,
          from_name: "RewardIdPage",
          user_id: this.user ? this.user.id : null,
          old_json: JSON.stringify(oldData),
          new_json: JSON.stringify(newData),
        };
        await axios.post(`/user_timestamps`, form);
      } catch (error) {
        console.log(error);
      }
    },

    handleSuccessClose() {
      this.successDialog = false;
      this.$router.back();
    },
  },
};
</script>
